import {type KeyboardEvent, useCallback, useEffect, useRef} from "react";
import {radioOptionForArrowKey} from "./radioGroup";

/**
 * The keyboard half of a radio group built from {@link ./OfferOption} rows: arrow keys move the
 * selection, and focus follows it.
 *
 * Each row owns the roving tab stop (only the checked one is in the tab order), so without this the
 * unchecked rows would be unreachable from the keyboard. Which option is next is
 * {@link ./radioGroup.radioOptionForArrowKey}'s answer; this hook only wires it to the DOM.
 *
 * Focus moves only while the group already holds it: moving it on the first render would steal it
 * from the dialog, and the reader would land mid-card instead of at its start.
 */
export function useRadioGroupKeys<T>(options: readonly T[], current: T | null, select: (option: T) => void) {
	const selectedRef = useRef<HTMLButtonElement | null>(null);
	const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
		const next = radioOptionForArrowKey(event.key, options, current);
		if (next === null) {
			return;
		}
		// The group answers these keys, so the page must not also scroll on them.
		event.preventDefault();
		select(next);
	}, [options, current, select]);
	useEffect(() => {
		const active = document.activeElement;
		if (active instanceof HTMLElement && active.getAttribute("role") === "radio") {
			selectedRef.current?.focus();
		}
	}, [current]);
	return {onKeyDown, selectedRef};
}
